import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { MOIS_LABELS } from '../../utils/calculs'

const CAPACITE = { SPA: 42, HYDRO: 7 }

function pct(val, max) {
  if (!max) return 0
  return Math.min(100, Math.round((val / max) * 100))
}

function BarreTO({ valeur, max, couleur, label }) {
  const p = pct(valeur, max)
  return (
    <div style={{marginBottom:12}}>
      <div style={{display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4}}>
        <span style={{color:'var(--stone)'}}>{label}</span>
        <span style={{fontWeight:500}}>{valeur} / {max} <span style={{color:'var(--stone)'}}>({p}%)</span></span>
      </div>
      <div style={{background:'var(--border)', borderRadius:4, height:8, overflow:'hidden'}}>
        <div style={{width:`${p}%`, background: couleur, height:'100%', borderRadius:4, transition:'width 0.4s ease'}} />
      </div>
    </div>
  )
}

export default function TauxOccupation() {
  const now = new Date()
  const [annee, setAnnee] = useState(now.getFullYear())
  const [mois, setMois] = useState(now.getMonth() + 1)
  // Année scolaire : de septembre (anneeScolaire) à août (anneeScolaire + 1)
  const [anneeScolaire, setAnneeScolaire] = useState(now.getMonth() + 1 >= 9 ? now.getFullYear() : now.getFullYear() - 1)
  const [vue, setVue] = useState('mois') // 'mois' | 'jour' | 'annee'
  const [seances, setSeances] = useState([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    let debut, fin
    if (vue === 'annee') {
      debut = `${anneeScolaire}-09-01`
      fin = `${anneeScolaire + 1}-08-31`
    } else {
      debut = `${annee}-${String(mois).padStart(2,'0')}-01`
      // Fix bug UTC : construction explicite sans toISOString()
      const dernierJour = new Date(annee, mois, 0).getDate()
      fin = `${annee}-${String(mois).padStart(2,'0')}-${String(dernierJour).padStart(2,'0')}`
    }
    // Pagination : Supabase plafonne à 1000 lignes par requête
    const PAGE = 1000
    let toutes = []
    for (let from = 0; ; from += PAGE) {
      const { data } = await supabase.from('seances')
        .select('date, pole, total_massage_payant, total_massage_gratuit, total_massage')
        .gte('date', debut).lte('date', fin).order('date')
        .range(from, from + PAGE - 1)
      toutes = toutes.concat(data || [])
      if (!data || data.length < PAGE) break
    }
    setSeances(toutes)
    setLoading(false)
  }, [annee, mois, vue, anneeScolaire])

  useEffect(() => { load() }, [load])

  // Stats de la période affichée (mois ou année scolaire) par pôle.
  // Seuls les jours saisis comptent dans la capacité (jours fermés exclus).
  const calcStats = (liste) => {
    const res = {}
    for (const pole of ['SPA', 'HYDRO']) {
      const s = liste.filter(x => x.pole === pole)
      const nbJours = s.length
      const cap = CAPACITE[pole]
      const capMois = cap * nbJours
      const payants = s.reduce((a, x) => a + (x.total_massage_payant || 0), 0)
      const gratuits = s.reduce((a, x) => a + (x.total_massage_gratuit || 0), 0)
      const total = payants + gratuits
      res[pole] = { nbJours, cap, capMois, payants, gratuits, total }
    }
    return res
  }
  const statsMois = calcStats(seances)

  // Détail par mois pour la vue annuelle (septembre à août)
  const moisScolaires = [9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8].map(m => {
    const y = m >= 9 ? anneeScolaire : anneeScolaire + 1
    const cle = `${y}-${String(m).padStart(2, '0')}`
    return { m, y, cle, stats: calcStats(seances.filter(x => x.date.slice(0, 7) === cle)) }
  })
  const labelAnnee = `${anneeScolaire}-${anneeScolaire + 1}`

  // Stats par jour
  const parJour = {}
  for (const s of seances) {
    if (!parJour[s.date]) parJour[s.date] = {}
    parJour[s.date][s.pole] = s
  }
  const jours = Object.keys(parJour).sort()

  const annees = [2024, 2025, 2026, 2027]

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Taux d'occupation</h2>
          <div className="page-header-sub">Capacité SPA : {CAPACITE.SPA} soins/j · Hydro : {CAPACITE.HYDRO} soins/j</div>
        </div>
      </div>

      <div style={{display:'flex', gap:12, marginBottom:16, flexWrap:'wrap'}}>
        {vue === 'annee' ? (
          <select value={anneeScolaire} onChange={e => setAnneeScolaire(+e.target.value)}
            style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
            {annees.map(a => <option key={a} value={a}>Année scolaire {a}-{a + 1}</option>)}
          </select>
        ) : (
          <>
            <select value={annee} onChange={e => setAnnee(+e.target.value)}
              style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
              {annees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={mois} onChange={e => setMois(+e.target.value)}
              style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
              {MOIS_LABELS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </>
        )}
      </div>

      <div className="tabs" style={{marginBottom:24}}>
        <button className={`tab-btn ${vue === 'mois' ? 'active' : ''}`} onClick={() => setVue('mois')}>Vue mensuelle</button>
        <button className={`tab-btn ${vue === 'jour' ? 'active' : ''}`} onClick={() => setVue('jour')}>Vue par journée</button>
        <button className={`tab-btn ${vue === 'annee' ? 'active' : ''}`} onClick={() => setVue('annee')}>Vue annuelle</button>
      </div>

      {loading ? <div className="spinner" /> : (
        vue === 'annee' ? (
          <>
            <div className="stats-grid" style={{marginBottom:24}}>
              {['SPA', 'HYDRO'].map(pole => {
                const s = statsMois[pole]
                return (
                  <div className="stat-card" key={pole}>
                    <div className="s-label">TO global {pole} — {labelAnnee}</div>
                    <div className="s-value">{pct(s.total, s.capMois)} %</div>
                    <div className="s-sub">{s.total} soins / {s.capMois} places ({s.nbJours} jours)</div>
                  </div>
                )
              })}
              {['SPA', 'HYDRO'].map(pole => {
                const s = statsMois[pole]
                return (
                  <div className="stat-card" key={pole + '_pay'}>
                    <div className="s-label">TO payant {pole}</div>
                    <div className="s-value">{pct(s.payants, s.total)} %</div>
                    <div className="s-sub">{s.payants} payants / {s.total} soins réalisés</div>
                  </div>
                )
              })}
            </div>

            {['SPA', 'HYDRO'].map(pole => {
              const s = statsMois[pole]
              if (s.nbJours === 0) return null
              return (
                <div className="card" key={pole} style={{marginBottom:16}}>
                  <h3 style={{marginBottom:20}}>Pôle {pole} — Année scolaire {labelAnnee}</h3>
                  <BarreTO valeur={s.total} max={s.capMois} couleur="var(--charcoal)" label="TO global (payant + gratuit)" />
                  <BarreTO valeur={s.payants} max={s.total} couleur="var(--bordeaux)" label="Part payant (sur soins réalisés)" />
                  <BarreTO valeur={s.gratuits} max={s.total} couleur="var(--gold)" label="Part gratuit (sur soins réalisés)" />
                  <div style={{marginTop:16, fontSize:12, color:'var(--stone)'}}>
                    Capacité journalière : {s.cap} soins · {s.nbJours} jour{s.nbJours > 1 ? 's' : ''} saisi{s.nbJours > 1 ? 's' : ''} · Capacité totale de l'année : {s.capMois} soins
                  </div>
                </div>
              )
            })}

            {seances.length > 0 && (
              <div className="card" style={{overflowX:'auto', marginBottom:16}}>
                <h3 style={{marginBottom:12}}>Détail par mois</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Mois</th>
                      <th className="num">Jours SPA</th>
                      <th className="num">Soins SPA</th>
                      <th className="num">TO SPA</th>
                      <th className="num">Payant SPA</th>
                      <th className="num">Jours Hydro</th>
                      <th className="num">Soins Hydro</th>
                      <th className="num">TO Hydro</th>
                      <th className="num">Payant Hydro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moisScolaires.map(({ m, y, cle, stats }) => {
                      const spa = stats.SPA
                      const hy = stats.HYDRO
                      const vide = <span className="text-muted">—</span>
                      return (
                        <tr key={cle}>
                          <td style={{whiteSpace:'nowrap'}}>{MOIS_LABELS[m]} {y}</td>
                          <td className="num">{spa.nbJours || vide}</td>
                          <td className="num">{spa.nbJours ? spa.total : vide}</td>
                          <td className="num">{spa.nbJours ? <strong>{pct(spa.total, spa.capMois)}%</strong> : vide}</td>
                          <td className="num">{spa.nbJours ? `${pct(spa.payants, spa.total)}%` : vide}</td>
                          <td className="num">{hy.nbJours || vide}</td>
                          <td className="num">{hy.nbJours ? hy.total : vide}</td>
                          <td className="num">{hy.nbJours ? <strong>{pct(hy.total, hy.capMois)}%</strong> : vide}</td>
                          <td className="num">{hy.nbJours ? `${pct(hy.payants, hy.total)}%` : vide}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>Total année</strong></td>
                      <td className="num"><strong>{statsMois.SPA.nbJours}</strong></td>
                      <td className="num"><strong>{statsMois.SPA.total}</strong></td>
                      <td className="num"><strong style={{color:'var(--bordeaux)'}}>{pct(statsMois.SPA.total, statsMois.SPA.capMois)}%</strong></td>
                      <td className="num"><strong>{pct(statsMois.SPA.payants, statsMois.SPA.total)}%</strong></td>
                      <td className="num"><strong>{statsMois.HYDRO.nbJours}</strong></td>
                      <td className="num"><strong>{statsMois.HYDRO.total}</strong></td>
                      <td className="num"><strong style={{color:'var(--bordeaux)'}}>{pct(statsMois.HYDRO.total, statsMois.HYDRO.capMois)}%</strong></td>
                      <td className="num"><strong>{pct(statsMois.HYDRO.payants, statsMois.HYDRO.total)}%</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {seances.length === 0 && (
              <div className="alert alert-info">Aucune séance saisie pour l'année scolaire {labelAnnee}.</div>
            )}
          </>
        ) : vue === 'mois' ? (
          <>
            {/* Stats cards */}
            <div className="stats-grid" style={{marginBottom:24}}>
              {['SPA', 'HYDRO'].map(pole => {
                const s = statsMois[pole]
                return (
                  <div className="stat-card" key={pole}>
                    <div className="s-label">TO global {pole} — {MOIS_LABELS[mois]}</div>
                    <div className="s-value">{pct(s.total, s.capMois)} %</div>
                    <div className="s-sub">{s.total} soins / {s.capMois} places ({s.nbJours} jours)</div>
                  </div>
                )
              })}
              {['SPA', 'HYDRO'].map(pole => {
                const s = statsMois[pole]
                return (
                  <div className="stat-card" key={pole + '_pay'}>
                    <div className="s-label">TO payant {pole}</div>
                    <div className="s-value">{pct(s.payants, s.total)} %</div>
                    <div className="s-sub">{s.payants} payants / {s.total} soins réalisés</div>
                  </div>
                )
              })}
            </div>

            {/* Barres visuelles */}
            {['SPA', 'HYDRO'].map(pole => {
              const s = statsMois[pole]
              if (s.nbJours === 0) return null
              return (
                <div className="card" key={pole} style={{marginBottom:16}}>
                  <h3 style={{marginBottom:20}}>Pôle {pole} — {MOIS_LABELS[mois]} {annee}</h3>
                  <BarreTO valeur={s.total} max={s.capMois} couleur="var(--charcoal)" label="TO global (payant + gratuit)" />
                  <BarreTO valeur={s.payants} max={s.total} couleur="var(--bordeaux)" label="Part payant (sur soins réalisés)" />
                  <BarreTO valeur={s.gratuits} max={s.total} couleur="var(--gold)" label="Part gratuit (sur soins réalisés)" />
                  <div style={{marginTop:16, fontSize:12, color:'var(--stone)'}}>
                    Capacité journalière : {s.cap} soins · {s.nbJours} jour{s.nbJours > 1 ? 's' : ''} saisi{s.nbJours > 1 ? 's' : ''} · Capacité totale du mois : {s.capMois} soins
                  </div>
                </div>
              )
            })}

            {seances.length === 0 && (
              <div className="alert alert-info">Aucune séance saisie pour {MOIS_LABELS[mois]} {annee}.</div>
            )}
          </>
        ) : (
          /* Vue par journée */
          <div className="card" style={{overflowX:'auto'}}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">SPA payants</th>
                  <th className="num">SPA gratuits</th>
                  <th className="num">TO SPA</th>
                  <th className="num">Hydro payants</th>
                  <th className="num">Hydro gratuits</th>
                  <th className="num">TO Hydro</th>
                </tr>
              </thead>
              <tbody>
                {jours.length === 0 ? (
                  <tr><td colSpan={7} style={{textAlign:'center', color:'var(--stone)', padding:32}}>Aucune séance</td></tr>
                ) : jours.map(date => {
                  const spa = parJour[date]['SPA']
                  const hydro = parJour[date]['HYDRO']
                  const toSpa = spa ? pct(spa.total_massage, CAPACITE.SPA) : null
                  const toHydro = hydro ? pct(hydro.total_massage, CAPACITE.HYDRO) : null
                  return (
                    <tr key={date}>
                      <td style={{whiteSpace:'nowrap'}}>
                        {new Date(date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="num">{spa ? spa.total_massage_payant : <span className="text-muted">—</span>}</td>
                      <td className="num">{spa ? spa.total_massage_gratuit : <span className="text-muted">—</span>}</td>
                      <td className="num">
                        {toSpa !== null ? (
                          <span style={{
                            color: toSpa >= 80 ? 'var(--success)' : toSpa >= 50 ? 'var(--gold)' : 'var(--bordeaux)',
                            fontWeight: 500
                          }}>{toSpa}%</span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td className="num">{hydro ? hydro.total_massage_payant : <span className="text-muted">—</span>}</td>
                      <td className="num">{hydro ? hydro.total_massage_gratuit : <span className="text-muted">—</span>}</td>
                      <td className="num">
                        {toHydro !== null ? (
                          <span style={{
                            color: toHydro >= 80 ? 'var(--success)' : toHydro >= 50 ? 'var(--gold)' : 'var(--bordeaux)',
                            fontWeight: 500
                          }}>{toHydro}%</span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {jours.length > 0 && (
                <tfoot>
                  <tr>
                    <td><strong>Total mois</strong></td>
                    <td className="num"><strong>{statsMois.SPA.payants}</strong></td>
                    <td className="num"><strong>{statsMois.SPA.gratuits}</strong></td>
                    <td className="num"><strong style={{color:'var(--bordeaux)'}}>{pct(statsMois.SPA.total, statsMois.SPA.capMois)}%</strong></td>
                    <td className="num"><strong>{statsMois.HYDRO.payants}</strong></td>
                    <td className="num"><strong>{statsMois.HYDRO.gratuits}</strong></td>
                    <td className="num"><strong style={{color:'var(--bordeaux)'}}>{pct(statsMois.HYDRO.total, statsMois.HYDRO.capMois)}%</strong></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )
      )}
    </div>
  )
}
